import { memo, ReactNode, useEffect, useState } from "react";

import { Button, CircularProgress, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import CloseCircle from "@zenith/assets/icons/CloseCircle";
import Close from "@zenith/assets/icons/Close";
import MajorButtonComponent from "@zenith/components/ui/MajorButtonComponent/major-button-component/major-button-component";
import WarningIcon from "@zenith/assets/icons/components/WarningIcon";
import { HexagonalWarning } from "@zenith/assets/icons/HexagonalWarning";
import ButtonComponent from "../../Button/button-component";

/**
 * Props for Modal Component.
 * @interface
 * @name ModalComponentProps
 * @kind interface
 * @exports
 */
export interface ModalComponentProps {
  /**
   * Content for the Modal.
   * @property
   * @name body
   * @kind property
   * @memberof ModalComponentProps
   * @type {ReactNode}
   */
  body: string | ReactNode;
  /**
   * If `true`, shows the modal.
   * @default false
   * @property
   * @name showModal
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean}
   */
  showModal: boolean;
  /**
   * Title for the Modal.
   * @property
   * @name title
   * @kind property
   * @memberof ModalComponentProps
   * @type {string}
   */
  title: string;
  /**
   * (optional)
   * Callback fired when the single button is clicked.
   * @method
   * @name onClickButton
   * @kind property
   * @memberof ModalComponentProps
   */
  onClickButton?: () => void;
  /**
   * Callback fired when the close button is clicked.
   * @method
   * @name onClickClose
   * @kind property
   * @memberof ModalComponentProps
   * @param {object} event?
   * @param {string} reason?
   * @returns {void}
   */
  onClickClose?: (event?: object, reason?: string) => void;
  /**
   * (optional)
   * Text for the single button.
   * @property
   * @name buttonText
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  buttonText?: string;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name classNames
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  classNames?: string;
  /**
   * (optional)
   * If `true`, shows two action buttons instead of one.
   * @property
   * @name doubleOptions
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */
  doubleOptions?: boolean;
  /**
   * (optional)
   * If `doubleOptions` is `true`,
   * shows text for the left action button.
   * @property
   * @name leftButtonText
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  leftButtonText?: string;
  /**
   * (optional)
   * If `doubleOptions` is `true`,
   * shows text for the right action button.
   * @property
   * @name rightButtonText
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  rightButtonText?: string;
  /**
   * (optional)
   * Second line text for the modal..
   * @property
   * @name secondLine
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  secondLine?: string | ReactNode;
  /**
   * (optional)
   * If `true`, shows the error icon.
   * @default true
   * @property
   * @name showErrorIcon
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */
  showErrorIcon?: boolean;
  /**
   * (optional)
   * If `showErrorIcon` is `true`,
   * the icon can be `"error"`, `"warning"` or `"hexagonalWarning"`.
   * @property
   * @name icon
   * @kind property
   * @memberof ModalComponentProps
   * @type {"error" | "warning" | "hexagonalWarning" | undefined}
   */
  icon?: "error" | "warning" | "hexagonalWarning";
  /**
   * (optional)
   * Custom node rendered in the icon slot, above the title, instead of the
   * built-in error/warning icons. Takes precedence over `showErrorIcon`.
   * @property
   * @name iconNode
   * @kind property
   * @memberof ModalComponentProps
   * @type {ReactNode | undefined}
   */
  iconNode?: ReactNode;
  /**
   * (optional)
   * Overrides the body wrapper classes. Use when the body carries its own
   * layout (full-width cards, custom alignment) rather than centred text.
   * @property
   * @name bodyClassName
   * @kind property
   * @memberof ModalComponentProps
   * @type {string | undefined}
   */
  bodyClassName?: string;
  /**
   * (optional)
   * If `doubleOptions` is `true`,
   * this callback is fired when the left button is clicked.
   * @method
   * @name onLeftButtonClick
   * @kind property
   * @memberof ModalComponentProps
   */
  onLeftButtonClick?: () => void;
  /**
   * (optional)
   * If `doubleOptions` is `true`,
   * this callback is fired when the right button is clicked.
   * @method
   * @name onRightButtonClick
   * @kind property
   * @memberof ModalComponentProps
   */
  onRightButtonClick?: () => void;
  /**
   * (optional)
   * If `true`, shows the wider button for the single action button.
   * @property
   * @name widerButton
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */
  widerButton?: boolean;
  /**
   * (optional)
   * If `true`, disables all modal action buttons.
   * @property
   * @name disabledButtons
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */
  disabledButtons?: boolean;
  /**
   * (optional)
   * If `true`,
   * shows the left action button loading.
   * @property
   * @name isLeftLoading
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */
  isLeftLoading?: boolean;
  /**
   * (optional)
   * If `true`,
   * shows the right action button loading.
   * @property
   * @name isRightLoading
   * @kind property
   * @memberof ModalComponentProps
   * @type {boolean | undefined}
   */

  /**
   * (optional)
   * Custom size for the modal.
   * @property
   * @name size
   * @kind property
   * @memberof ModalComponentProps
   * @type {"small" | "medium" | "large" | "semi-large" | { width: string; height: string } | undefined}
   */
  size?:
    | "small"
    | "medium"
    | "large"
    | "semi-large"
    | { width: string; height: string };
  isRightLoading?: boolean;
  showCloseIcon?: boolean;
  hideButtons?: boolean;
  spaceBetween?: boolean;
  alignTitleStart?: boolean;
}

/**
 * Memoized Modal Component.
 * @constant
 * @name ModalComponent
 * @kind variable
 * @type {React.NamedExoticComponent<ModalComponentProps>}
 */
const ModalComponent: React.NamedExoticComponent<ModalComponentProps> = memo(
  /**
   * Modal Component.
   * @function
   * @name ModalComponent
   * @kind function
   * @memberof ModalComponent
   * @param {ModalComponentProps} props Props for Modal Component.
   * @returns {JSX.Element} Modal Component.
   */
  function ModalComponent(props: ModalComponentProps): JSX.Element {
    const {
      body,
      size = "medium",
      onClickButton,
      onClickClose,
      showCloseIcon = true,
      showModal,
      title,
      buttonText,
      classNames,
      disabledButtons,
      doubleOptions,
      leftButtonText,
      onLeftButtonClick,
      onRightButtonClick,
      rightButtonText,
      secondLine,
      showErrorIcon = true,
      icon = "error",
      iconNode,
      bodyClassName,
      widerButton,
      isLeftLoading,
      isRightLoading,
      hideButtons,
      spaceBetween,
      alignTitleStart,
    } = props;
    const [open, setOpen] = useState(showModal);

    useEffect(() => {
      setOpen(showModal);
    }, [showModal]);

    const renderIcon = () => {
      if (iconNode) return <>{iconNode}</>;
      if (!showErrorIcon) return null;
      switch (icon) {
        case "warning":
          return <WarningIcon width="40" height="40" className="my-3" />;
        case "hexagonalWarning":
          return (
            <HexagonalWarning
              width="40"
              height="40"
              viewBox="0 0 48 48"
              className="my-3"
            />
          );
        case "error":
        default:
          return <CloseCircle width="40" height="40" className="my-3" />;
      }
    };

    const getSizeStyles = () => {
      if (typeof size === "object") {
        return `w-[${size.width}] h-[${size.height}]`;
      }
      switch (size) {
        case "small":
          return "h-50 w-full md:w-2/6";
        case "medium":
          return "h-250 px-2 w-4/6 md:w-100";
        case "semi-large":
          return "h-250 px-10 w-10/12 md:w-6/12 lg:w-5/12";
        case "large":
          return " py-12 px-8 w-5/6 md:w-3/6";
        default:
          return "h-250 px-2 w-4/6 md:w-100";
      }
    };

    return (
      <div>
        <Modal
          open={open}
          aria-labelledby="modal-modal-title"
          onClose={onClickClose}
          aria-describedby="modal-modal-description"
        >
          <Box
            className={`absolute top-1/2 left-1/2  flex w-11/12 -translate-x-1/2 -translate-y-1/2 transform flex-col ${
              spaceBetween ? "justify-between" : "items-center"
            } rounded-3xl bg-white outline-none ${classNames} ${getSizeStyles()} ${
              !showCloseIcon ? "pt-12" : ""
            }`}
          >
            {showCloseIcon && (
              <IconButton className="mr-3 mt-5 self-end" onClick={onClickClose}>
                <Close />
              </IconButton>
            )}
            {renderIcon()}
            <h5
              className={` text-xl font-semibold ${
                showErrorIcon ? "" : "mt-2"
              } ${alignTitleStart ? "mx-6 text-start" : "mx-3 text-center"}`}
            >
              {title}
            </h5>
            <div
              className={
                bodyClassName ??
                "mx-2 mt-4 mb-8 text-center text-sm text-neutral-600"
              }
            >
              {typeof body === "string" ? <p>{body}</p> : body}
              {secondLine && <p>{secondLine}</p>}
            </div>

            {/* Hide buttons if hideButtons is true */}
            {!hideButtons && (
              <div
                className={
                  doubleOptions
                    ? "my-8 flex w-full grow items-end justify-between gap-2 px-4"
                    : "mb-6 flex grow items-end"
                }
              >
                {doubleOptions ? (
                  <>
                    <ButtonComponent
                      label={leftButtonText!}
                      fullWidth
                      onClick={onLeftButtonClick}
                      disabled={disabledButtons || isLeftLoading}
                      size="large"
                      type="secondary"
                      id="modal-left-button"
                      isLoading={isLeftLoading}
                    />

                    <ButtonComponent
                      label={rightButtonText!}
                      fullWidth
                      onClick={onRightButtonClick}
                      disabled={disabledButtons}
                      size="large"
                      type="primary"
                      id="modal-right-button"
                      isLoading={isRightLoading}
                    />
                  </>
                ) : (
                  <ButtonComponent
                    label={buttonText || ""}
                    fullWidth
                    onClick={onClickButton}
                    disabled={disabledButtons}
                    size="large"
                    type="primary"
                    id="continue-button"
                  />
                )}
              </div>
            )}
          </Box>
        </Modal>
      </div>
    );
  }
);

export default ModalComponent;
